import firebase from 'firebase/functions';
import { Collection, Inventory } from 'lib/documents';
import {
  AssessmentIndexEntry,
  AssessmentType
} from 'lib/documents/patient-assessment/patient-assessment';
import {
  httpsCallableWrapper,
  typedHttpsCallableWrapper,
  typedOnRequestWrapper
} from '../api-_utils';
import {
  deserialiseAPIObject,
  parseAPIJSON_legacy,
  parseAPISafeJSON
} from '../parseAPIJSON';
import { components } from './api-config/api-paths-v2.ts.types';
import { API_ROUTE_CONFIG } from './api-config/config';

export const htmlToPDF = (
  html: string
): Promise<firebase.HttpsCallableResult> =>
  httpsCallableWrapper(API_ROUTE_CONFIG.pdfExport.pdfExport)({ html });
/**
 * Copied from backend
 */

export type PatientInfo = components['schemas']['PatientInfo'];

export const listPatientsWithAssessmentStatus = (options: ListUserAPIOptions) =>
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.list.patientsWithAssessmentStatus
  )(options);

export const getImpactTestURL = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.impact.getImPactTestURL
);

export const verifyEmail = (redirectUrl: string) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.auth.user.sendEmailVerification)({
    redirectUrl
  });

export interface ListUserAPIOptions {
  clinicId: string;
  pageSize?: number;
  cursor?: string;
  pendingUsers?: boolean;
}
export type ListUserAPIOutput<T> = {
  data: T[];
  cursor: string | null;
  errors: { message: string; status: string; userId: string }[];
};
export const listPatients = (options: ListUserAPIOptions) =>
  typedHttpsCallableWrapper(API_ROUTE_CONFIG.clinic.list.patients)(
    options
  ).then(parseAPISafeJSON);

export const listClinicians = (options: ListUserAPIOptions) =>
  typedHttpsCallableWrapper(API_ROUTE_CONFIG.clinic.list.clinicians)(
    options
  ).then(parseAPISafeJSON);

export const addPatient = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.invite.patient
);

export const addClinician = (data: {
  firstName: string;
  lastName: string;
  email: string;
  clinicId: string;
}) => httpsCallableWrapper(API_ROUTE_CONFIG.clinic.invite.clinician)(data);

export const getClinicBasicInfo = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.info.get.basic
);

export const clinicActionResendInvitation = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.action.resendInvitation
);

export const clinicActionEditPatientDetails = (args: {
  patientId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  assessmentType: AssessmentType;
  clinicianId: string;
  clinicId: string;
}) =>
  httpsCallableWrapper<
    {
      patientId: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNumber?: string | null;
      clinicianId: string;
      clinicId: string;
    },
    'Ok'
  >(API_ROUTE_CONFIG.clinic.action.patient.editDetails)(args).then(
    parseAPIJSON_legacy
  );

export const clinicActionGetInvitationLink = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.action.getInvitationLink
);

export const clinicActionEditClinicianDetails = (args: {
  uid: string;
  firstName: string;
  lastName: string;
  email: string;
}) =>
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.action.clinician.editDetails
  )(args).then(parseAPIJSON_legacy);

export const invokeEnhancedTreatmentRecommendations = (patientId: string) =>
  httpsCallableWrapper(
    API_ROUTE_CONFIG.assessment.enhancedTreatmentRecommendations
  )({
    patientId
  });

export const clinicInfoEdit = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.info.edit
);

export const clinicInfoEditRequestImageUploadURL = (clinicInfo: {
  clinicId: string;
  fileType: 'png' | 'jpeg';
}) =>
  httpsCallableWrapper<any, { expires: string; url: string }>(
    API_ROUTE_CONFIG.clinic.info.editRequestImageUploadURL
  )(clinicInfo).then(parseAPIJSON_legacy);

export const clinicGetAllInfo =
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.info.get.all
  );

export const invokeCalculatePatientSubtype = (options: {
  patientId: string;
  collection:
    | Collection.PendingPatientAssessments
    | Collection.PatientAssessments;
  destId: string;
}) =>
  httpsCallableWrapper(
    API_ROUTE_CONFIG.assessment.invokeCalculatePatientSubtype
  )(options);

export const assessmentSubmitInventory = (
  inventoryName: string,
  inventoryData: Omit<Inventory, 'date' | 'id'>
) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.assessment.submitInventory)({
    inventory: inventoryName,
    inventoryData
  });

export const assessmentGetPendingAssessment = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.assessment.pending.get.getPendingAssessment
);

export const changeAssessmentType = (
  patientId: string,
  assessmentType: AssessmentType
) =>
  httpsCallableWrapper(API_ROUTE_CONFIG.assessment.changeAssessmentType)({
    patientId,
    assessmentType
  });

export const getClinicianData = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinician.getClinicianData
);
export const listPatientAssessmentIndex = (patientId: string) =>
  httpsCallableWrapper<{ patientId: string }, AssessmentIndexEntry[]>(
    API_ROUTE_CONFIG.patient.list.patientAssessmentIndex
  )({ patientId }).then(_ => deserialiseAPIObject(_.data));

/**
 * Create a new clinic (organisation)
 */
export const apiClinicCreate = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.create
);

/**
 * Invite a clinic admin to a clinic (organisation)
 */
export const apiInviteClinicAdmin = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.invite.clinicadmin
);

export const apiClinicList = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.list.clinics
);

export const apiClinicOnboardingRegisterUser = typedOnRequestWrapper(
  API_ROUTE_CONFIG.clinic.onboarding.registerUser
);

export const apiClinicOnboardingRegisterOrganisation =
  typedHttpsCallableWrapper(
    API_ROUTE_CONFIG.clinic.onboarding.registerOrganisation
  );
export const apiClinicGetClinicAdminData = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinician.get.clinicAdmin
);

export const apiClinicOnboardingGenerateToken = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.onboarding.generateToken
);
export const apiClinicOnboardingValidateToken = typedOnRequestWrapper(
  API_ROUTE_CONFIG.clinic.onboarding.validateToken
);
export const apiClinicOnboardingClaimToken = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.onboarding.claimToken
);
export const apiClinicOnboardingCheckStatus = typedHttpsCallableWrapper(
  API_ROUTE_CONFIG.clinic.onboarding.checkStatus
);
